import {
  type Icon,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/theme-provider';
import { Mascot, MascotVariant } from './Mascot';

export interface EmptyStateProps {
  title: string;
  description: string;
  icon: Icon;
  actionLabel?: string;
  onAction?: () => void;
  actionSelector?: string;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  secondaryActionSelector?: string;
  filterActive?: boolean;
  onClearFilter?: () => void;
  fullScreen?: boolean;
  className?: string;
  mascot?: MascotVariant;
  mascotSize?: 'sm' | 'md' | 'lg' | 'xl';
}

/** Compact empty state for inline sections like sprint task lists */
export interface CompactEmptyStateProps {
  title?: string;
  description?: string;
  className?: string;
}

export function CompactEmptyState({
  title = 'No tasks yet',
  description = 'Tasks added to this section will appear here.',
  className,
}: CompactEmptyStateProps) {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center py-8 px-4 text-center',
      'opacity-0 animate-in fade-in-0 duration-300 fill-mode-forwards',
      className
    )}>
      <Mascot variant="empty" size="sm" className="mb-3" />
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <p className="text-xs text-muted-foreground/70 mt-1 max-w-[250px]">{description}</p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon: Icon,
  actionLabel,
  onAction,
  actionSelector,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionSelector,
  filterActive,
  onClearFilter,
  fullScreen = false,
  className = '',
  mascot,
  mascotSize = 'lg',
}: EmptyStateProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else if (actionSelector) {
      const element = document.querySelector(actionSelector);
      if (element instanceof HTMLElement) {
        element.click();
      }
    }
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    } else if (secondaryActionSelector) {
      const element = document.querySelector(secondaryActionSelector);
      if (element instanceof HTMLElement) {
        element.click();
      }
    }
  };

  return (
    <div
      className={cn(
        "w-full flex flex-col items-center justify-center text-center",
        "animate-in fade-in duration-300",
        fullScreen ? "min-h-[80vh]" : "py-12",
        className
      )}
    >
      <div className="max-w-md">
        {mascot ? (
          <Mascot 
            variant={mascot} 
            size={mascotSize} 
            className="mb-6 delay-100" 
          />
        ) : (
          <div
            className={cn(
              "mx-auto flex h-16 w-16 items-center justify-center rounded-full",
              "ring-1 ring-border/50 shadow-sm",
              isDark ? "bg-muted/50" : "bg-primary/5"
            )}
          >
            <Icon className="h-7 w-7 text-primary" />
          </div>
        )}
        <h3 className={cn(
          "mt-5 text-xl font-semibold tracking-tight",
          "bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent"
        )}>
          {title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground/80 max-w-sm mx-auto font-normal">
          {description}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {filterActive && onClearFilter && (
            <Button 
              variant="outline" 
              onClick={onClearFilter}
              className="shadow-sm hover:shadow-md transition-all duration-200"
            >
              Clear Filters
            </Button>
          )}
          {actionLabel && (onAction || actionSelector) && (
            <Button
              onClick={handleAction}
              className="shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105"
            >
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && (onSecondaryAction || secondaryActionSelector) && (
            <Button
              variant="outline"
              onClick={handleSecondaryAction}
              className="shadow-sm hover:shadow-md transition-all duration-200"
            >
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
} 